import { z } from "zod";
import { DateOnlyStringSchema } from "./dates";

/**
 * Lifecycle states for an organization's onboarding plan. DRAFT plans are
 * invisible to the organization (staging), ACTIVE plans render the onboarding
 * tab and banner, and COMPLETED is terminal: the tab and banner disappear.
 */
export const ONBOARDING_PLAN_STATUS_VALUES = [
  "DRAFT",
  "ACTIVE",
  "COMPLETED",
] as const;

/** Zod schema validating an onboarding plan status. */
export const OnboardingPlanStatusSchema = z.enum(ONBOARDING_PLAN_STATUS_VALUES);

/** An onboarding plan lifecycle status value. */
export type OnboardingPlanStatus = z.infer<typeof OnboardingPlanStatusSchema>;

/** Enum-style accessor for onboarding plan status values. */
export const ONBOARDING_PLAN_STATUS = OnboardingPlanStatusSchema.enum;

/**
 * Kinds of onboarding tasks: regular onboarding TODOs and UAT steps that must
 * pass before go-live. UAT tasks render as their own group with a progress bar.
 */
export const ONBOARDING_TASK_TYPE_VALUES = ["TASK", "UAT"] as const;

/** Zod schema validating an onboarding task type. */
export const OnboardingTaskTypeSchema = z.enum(ONBOARDING_TASK_TYPE_VALUES);

/** An onboarding task type value. */
export type OnboardingTaskType = z.infer<typeof OnboardingTaskTypeSchema>;

/** Enum-style accessor for onboarding task type values. */
export const ONBOARDING_TASK_TYPE = OnboardingTaskTypeSchema.enum;

/**
 * Where an onboarding schedule phase takes place: remote sessions or an
 * on-site visit.
 */
export const ONBOARDING_PHASE_LOCATION_VALUES = ["REMOTE", "ONSITE"] as const;

/** Zod schema validating an onboarding phase location. */
export const OnboardingPhaseLocationSchema = z.enum(
  ONBOARDING_PHASE_LOCATION_VALUES,
);

/** An onboarding phase location value. */
export type OnboardingPhaseLocation = z.infer<
  typeof OnboardingPhaseLocationSchema
>;

/** Enum-style accessor for onboarding phase location values. */
export const ONBOARDING_PHASE_LOCATION = OnboardingPhaseLocationSchema.enum;

/**
 * Admin-selectable tone for the persistent onboarding banner while tasks are
 * still open. Each value maps to an existing design-system color token
 * (amber/red/blue/brand); the all-tasks-done celebration state is always green
 * and is not configurable.
 */
export const ONBOARDING_BANNER_COLOR_VALUES = [
  "WARNING",
  "DESTRUCTIVE",
  "INFO",
  "PRIMARY",
] as const;

/** Zod schema validating an onboarding banner color. */
export const OnboardingBannerColorSchema = z.enum(
  ONBOARDING_BANNER_COLOR_VALUES,
);

/** An onboarding banner color value. */
export type OnboardingBannerColor = z.infer<typeof OnboardingBannerColorSchema>;

/** Enum-style accessor for onboarding banner color values. */
export const ONBOARDING_BANNER_COLOR = OnboardingBannerColorSchema.enum;

/**
 * An organization's onboarding plan: the single per-org container for the
 * go-live schedule, tasks, and announcements shown on the onboarding tab.
 * The schedule range (`scheduleStart`–`scheduleEnd`) bounds the gantt
 * timeline; nothing renders on the calendar until both are set.
 */
export const OnboardingPlanSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  status: OnboardingPlanStatusSchema,
  goLiveDate: DateOnlyStringSchema.nullable(),
  scheduleStart: DateOnlyStringSchema.nullable(),
  scheduleEnd: DateOnlyStringSchema.nullable(),
  bannerMessage: z.string().nullable(),
  bannerColor: OnboardingBannerColorSchema,
  updatedByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** An organization's onboarding plan. */
export type OnboardingPlan = z.infer<typeof OnboardingPlanSchema>;

/**
 * A row on the go-live schedule gantt: a phase starting on `startDate` and
 * running until the next phase begins (the last phase runs to the plan's
 * scheduleEnd).
 */
export const OnboardingPhaseSchema = z.object({
  id: z.string(),
  planId: z.string(),
  title: z.string(),
  location: OnboardingPhaseLocationSchema,
  startDate: DateOnlyStringSchema,
  order: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** A phase row on the onboarding go-live schedule. */
export type OnboardingPhase = z.infer<typeof OnboardingPhaseSchema>;

/**
 * A checklist item on the onboarding board. Anyone in the org can flip
 * `completed`; the latest actor and time are stamped on the row, and the full
 * check/uncheck history is recorded as activity events.
 */
export const OnboardingTaskSchema = z.object({
  id: z.string(),
  planId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  type: OnboardingTaskTypeSchema,
  section: z.string().nullable(),
  order: z.number().int(),
  assigneeUserId: z.string().nullable(),
  phaseId: z.string().nullable(),
  linkUrl: z.string().nullable(),
  completed: z.boolean(),
  completedAt: z.coerce.date().nullable(),
  completedByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** A checklist item on the onboarding board. */
export type OnboardingTask = z.infer<typeof OnboardingTaskSchema>;

/**
 * A message from the Forgepoint team shown on the onboarding tab.
 * `isForgepointAuthor` is derived server-side from the author's email domain
 * and only drives the "Forgepoint" badge in the UI.
 */
export const OnboardingAnnouncementSchema = z.object({
  id: z.string(),
  planId: z.string(),
  body: z.string(),
  authorUserId: z.string(),
  isForgepointAuthor: z.boolean(),
  pinned: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** An announcement on the onboarding tab. */
export type OnboardingAnnouncement = z.infer<
  typeof OnboardingAnnouncementSchema
>;

/** Request body for creating or updating the organization's onboarding plan. */
export const UpsertOnboardingPlanBodySchema = z.object({
  status: OnboardingPlanStatusSchema.optional(),
  goLiveDate: DateOnlyStringSchema.nullable().optional(),
  scheduleStart: DateOnlyStringSchema.nullable().optional(),
  scheduleEnd: DateOnlyStringSchema.nullable().optional(),
  bannerMessage: z.string().nullable().optional(),
  bannerColor: OnboardingBannerColorSchema.optional(),
});

/** Request body for creating or updating the organization's onboarding plan. */
export type UpsertOnboardingPlanBody = z.infer<
  typeof UpsertOnboardingPlanBodySchema
>;

/** Request body for creating an onboarding phase. */
export const CreateOnboardingPhaseBodySchema = z.object({
  title: z.string().min(1),
  location: OnboardingPhaseLocationSchema,
  startDate: DateOnlyStringSchema,
  order: z.number().int(),
});

/** Request body for creating an onboarding phase. */
export type CreateOnboardingPhaseBody = z.infer<
  typeof CreateOnboardingPhaseBodySchema
>;

/** Request body for updating an onboarding phase. */
export const UpdateOnboardingPhaseBodySchema =
  CreateOnboardingPhaseBodySchema.partial();

/** Request body for updating an onboarding phase. */
export type UpdateOnboardingPhaseBody = z.infer<
  typeof UpdateOnboardingPhaseBodySchema
>;

/** Request body for creating an onboarding task. */
export const CreateOnboardingTaskBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  type: OnboardingTaskTypeSchema.optional(),
  section: z.string().nullable().optional(),
  order: z.number().int(),
  assigneeUserId: z.string().nullable().optional(),
  phaseId: z.string().nullable().optional(),
  linkUrl: z.string().nullable().optional(),
});

/** Request body for creating an onboarding task. */
export type CreateOnboardingTaskBody = z.infer<
  typeof CreateOnboardingTaskBodySchema
>;

/**
 * Request body for updating an onboarding task. A body containing only
 * `completed` is a check-off and requires `onboarding.interact`; any other
 * field requires `settings.onboarding.edit`.
 */
export const UpdateOnboardingTaskBodySchema =
  CreateOnboardingTaskBodySchema.partial().extend({
    completed: z.boolean().optional(),
  });

/** Request body for updating an onboarding task. */
export type UpdateOnboardingTaskBody = z.infer<
  typeof UpdateOnboardingTaskBodySchema
>;

/** Request body for batch-reordering onboarding tasks. */
export const ReorderOnboardingTasksBodySchema = z.object({
  tasks: z.array(z.object({ id: z.string(), order: z.number().int() })),
});

/** Request body for batch-reordering onboarding tasks. */
export type ReorderOnboardingTasksBody = z.infer<
  typeof ReorderOnboardingTasksBodySchema
>;

/** Request body for creating an onboarding announcement. */
export const CreateOnboardingAnnouncementBodySchema = z.object({
  body: z.string().min(1),
  pinned: z.boolean().optional(),
});

/** Request body for creating an onboarding announcement. */
export type CreateOnboardingAnnouncementBody = z.infer<
  typeof CreateOnboardingAnnouncementBodySchema
>;

/** Request body for updating an onboarding announcement. */
export const UpdateOnboardingAnnouncementBodySchema =
  CreateOnboardingAnnouncementBodySchema.partial();

/** Request body for updating an onboarding announcement. */
export type UpdateOnboardingAnnouncementBody = z.infer<
  typeof UpdateOnboardingAnnouncementBodySchema
>;

/** Response for GET /onboarding: the organization's full onboarding board. */
export const OnboardingBoardResponseSchema = z.object({
  plan: OnboardingPlanSchema.nullable(),
  phases: z.array(OnboardingPhaseSchema),
  tasks: z.array(OnboardingTaskSchema),
  announcements: z.array(OnboardingAnnouncementSchema),
});

/** The organization's full onboarding board. */
export type OnboardingBoardResponse = z.infer<
  typeof OnboardingBoardResponseSchema
>;

/**
 * Response for GET /onboarding/summary: the lightweight payload the app-shell
 * banner renders from. `plan` is null when the org has no active plan, which
 * hides the banner and the onboarding tab.
 */
export const OnboardingSummaryResponseSchema = z.object({
  plan: z
    .object({
      status: OnboardingPlanStatusSchema,
      goLiveDate: DateOnlyStringSchema.nullable(),
      bannerMessage: z.string().nullable(),
      bannerColor: OnboardingBannerColorSchema,
    })
    .nullable(),
  openTaskCount: z.number().int(),
  totalTaskCount: z.number().int(),
  myOpenTaskCount: z.number().int(),
});

/** The lightweight onboarding summary the banner renders from. */
export type OnboardingSummaryResponse = z.infer<
  typeof OnboardingSummaryResponseSchema
>;
